import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerOrder } from "@/data/model/Customer";
import { Currency } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { OrderHelper } from "@/data/util/OrderHelper";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController, ViewTransition } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { FulfillmentPopover } from "@/app/popover/FulfillmentPopover";
import { Popup } from "@/app/ui/Popup";

export class OrderHistoryController extends BaseController {
    public onGoBack?: { () };
    public onClick?: { (model: ICustomerOrder) };

    private readonly $orderList: JQuery;
    private currentOffset = 0;
    private entriesPerPage = 40;
    private isLoading = false;

    constructor() {
        super("orderhistory");

        const hdr = $('<div class="head"><div class="title">' + trans.orderHistory.title + "</div></div>").appendTo(
            this.$view
        );
        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        this.$orderList = $('<div class="orders scrollable" />').appendTo(this.$view);
    }

    protected onBecomeVisible(param?: any, transition?: ViewTransition) {
        TastierClient.context.onFulfillmentsChanged.add(this.checkConfirmButtons, this);

        if (transition === undefined || transition === ViewTransition.Fade || transition === ViewTransition.SlideLeft) {
            TastierClient.context.loadFulfillmentsIfNeeded();

            this.isLoading = false;
            this.currentOffset = 0;
            this.$orderList.empty().scrollTop(0);

            this.fetchOrders();
        }

        this.bindLazyLoadScroll();
    }

    protected onBecomeInvisible() {
        this.unbindLazyLoadScroll();
        TastierClient.context.onFulfillmentsChanged.remove(this.checkConfirmButtons, this);
    }

    private async fetchOrders() {
        if (this.isLoading) return;

        this.isLoading = true;
        const loader = $(
            '<div class="loading"><div class="loader"></div>' + trans.orderHistory.loadingOrders + "</div>"
        ).appendTo(this.$orderList);

        try {
            const c = await Factory.getInstance<CustomerDao>(CustomerDao).getOrders({
                offset: this.currentOffset,
                limit: this.entriesPerPage,
            });
            loader.remove();

            const totalItems = this.currentOffset + c.items.length;
            if (totalItems === 0) {
                this.$orderList.append(
                    `<div class="nodata"><div class="lbl">${trans.orderHistory.noOrders}</div></div>`
                );
                this.unbindLazyLoadScroll();
                return;
            }

            for (const item of c.items) {
                this.appendOrder(item);
            }

            this.checkConfirmButtons();

            if (!c.hasNext) {
                this.unbindLazyLoadScroll();
            }

            this.currentOffset += c.items.length;
            this.isLoading = false;
        } catch (ex) {
            logError(ex);
            Popup.show("Failed to load orders.");
            this.isLoading = false;
            this.unbindLazyLoadScroll();
        }
    }

    private bindLazyLoadScroll() {
        this.$orderList.unbind("scroll.orderhistory").bind("scroll.orderhistory", () => {
            const lastE = this.$orderList.children().last();
            if (lastE.length > 0) {
                if (lastE.position().top < this.$orderList.height() + 50) this.fetchOrders();
            }
        });
    }

    private unbindLazyLoadScroll() {
        this.$orderList.unbind("scroll.orderhistory");
    }

    private checkConfirmButtons() {
        this.$orderList.find("> .order").each((i, e) => {
            const el = $(e);
            const order: ICustomerOrder = el.data("model");

            const btn = el.find("> .inner > .btn");
            if (TastierClient.context.currentFulfillments.find((c) => c.orderId == order.id) !== undefined) {
                if (btn.length === 0) {
                    Ripple.bind(
                        $('<div class="btn confirm">' + trans.orderHistory.confirmation + "</div>")
                            .appendTo(el.find("> .inner"))
                            .click((e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                const clickedOrder: ICustomerOrder = $(e.currentTarget).parent().parent().data("model");
                                new FulfillmentPopover({
                                    orderId: clickedOrder.id,
                                    orderNumber:
                                        clickedOrder.dailyOrderIncrement !== undefined
                                            ? OrderHelper.padDailyIncrement(clickedOrder.dailyOrderIncrement)
                                            : clickedOrder.orderNumber,
                                    locationName: clickedOrder.locationName,
                                    orderTime: clickedOrder.date,
                                    tableName: clickedOrder.tableName,
                                    orderServiceId: clickedOrder.orderServiceId,
                                    orderScheduledFulfillmentDateTime: clickedOrder.scheduledFulfillmentDateTime,
                                    preorder: clickedOrder.preorder,
                                }).show();
                            })
                    );
                }
            } else {
                if (btn.length !== 0) btn.remove();
            }
        });
    }

    private appendOrder(order: ICustomerOrder) {
        const o = $('<div class="order" />').data("model", order).appendTo(this.$orderList);
        const io = $('<div class="inner" />').appendTo(o);

        if (order.logoPath !== undefined)
            io.append('<div class="thumb"><img src="' + order.logoPath + '" alt="Logo" /></div>');

        const d = $('<div class="details" />').appendTo(io);
        d.append("<h3>" + Format.htmlEscape(order.locationName) + "</h3>");
        d.append(
            '<div class="date">' +
                DateTime.toDateString(order.date) +
                ", " +
                DateTime.toTimeString(order.date) +
                "</div>"
        );
        d.append(
            '<div class="consumption">' + Format.toMoneyFormat(order.consumption + order.tip, Currency.Chf) + "</div>"
        );

        Ripple.bind(
            o.click((e) => {
                e.preventDefault();
                e.stopPropagation();

                if (this.onClick !== undefined) this.onClick($(e.currentTarget).data("model"));
            })
        );
    }
}
